import { BaseTable } from "@/components/shared/data-table";
import { ServerSideDataTablePagination } from "@/components/shared/tables/server-side-table-pagination";
import { Children, isValidElement, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ErrorMessage } from "../../../../components/ui/error";
import type { AllTransactionsTableInstance } from "../use-all-transactions-table";
import { TransactionsActionBar } from "./all-transactions-action-bar";

const FiltersSlot = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

type AllTransactionsProps = AllTransactionsTableInstance & {
  handleMatch: () => Promise<void>;
  selectedInvoicesIds: Record<string, boolean>;
  children?: React.ReactNode;
};

export const AllTransactionsRoot = (props: AllTransactionsProps) => {
  const { t } = useTranslation();
  const tableRef = useRef(null);

  const {
    table,
    clickableColumns,
    columnVisibility,
    columns,
    data,
    stickyColumns,
    footer,
    loading,
    error,
    pagination,
    handleRowClick,
    handlePageChange,
    handlePageSizeChange,
    limit,
    handleMatch,
    selectedInvoicesIds,
    children,
  } = props;

  let filtersContent: React.ReactNode = null;

  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === FiltersSlot) {
      filtersContent = child.props.children;
    }
  });

  useEffect(() => {
    return () => {
      table.setFocusedRow(undefined);
    };
  }, [table]);

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <ErrorMessage message={error.message} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-row">
      <div
        className="transition-all duration-100 ease-in-out relative flex-1"
        id="transactions-table"
      >
        <div className="flex flex-col gap-2 h-full w-full">
          <p className="text-sm text-gray-600 mb-2 mt-2 font-medium">
            {t("transactions.all_guiding_message")}
          </p>
          {filtersContent}
          <BaseTable
            table={table}
            clickableColumns={clickableColumns}
            columnVisibility={columnVisibility}
            columns={columns}
            data={data}
            stickyColumns={stickyColumns}
            footer={footer}
            loading={loading}
            ref={tableRef}
            onRowClick={handleRowClick}
          />
          <TransactionsActionBar
            table={table}
            handleMatch={handleMatch}
            selectedInvoicesIds={selectedInvoicesIds}
          />
          <div className="mt-0.5" />
          <ServerSideDataTablePagination
            table={table}
            totalItems={pagination.totalItems}
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            pageSize={limit}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      </div>
    </div>
  );
};

export const AllTransactions = Object.assign(AllTransactionsRoot, {
  Filters: FiltersSlot,
});
